/// <reference path="./window_realtime_sse.d.ts" />
import {
  useRealtimeSSE,
  createRTKQueryInvalidationHandler,
  type ResourceInvalidation,
} from '@calendly/realtime-sse'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { meetingsGraphqlApi } from 'shared/api/meetings'
import { buildMeetingListTag, buildMeetingTag } from 'shared/api/meetings/constants'

import { setSSEConnectedForPolling } from '../sse_polling_state'

/** Default delay (ms) before invalidating cache after an SSE event to allow API to update. */
const DEFAULT_INVALIDATION_DEBOUNCE_MS = 5000
/** Allowed range for localStorage override: 0–30 seconds. */
const MIN_DEBOUNCE_MS = 0
const MAX_DEBOUNCE_MS = 30_000

const STORAGE_KEY_BASE_URL = 'realtime_sse_base_url'
const STORAGE_KEY_INVALIDATION_DEBOUNCE_MS = 'realtime_sse_invalidation_debounce_ms'
const STORAGE_KEY_SUBSCRIBE_PATH = 'realtime_sse_subscribe_path'
const CONFIG_CHANGE_EVENT = 'realtime_sse_config_change'

function readBaseUrlFromStorage(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const v = window.localStorage?.getItem(STORAGE_KEY_BASE_URL)
    return v && v.length > 0 ? v : null
  } catch {
    return null
  }
}

function readSubscribePathFromStorage(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const v = window.localStorage?.getItem(STORAGE_KEY_SUBSCRIBE_PATH)
    return v && v.length > 0 ? v : null
  } catch {
    return null
  }
}

/**
 * Parses invalidation debounce from localStorage. Returns a number in [MIN_DEBOUNCE_MS, MAX_DEBOUNCE_MS].
 * Safe against non-numeric, very long, or out-of-range values; uses default when invalid.
 */
function readInvalidationDebounceMsFromStorage(): number {
  if (typeof window === 'undefined') return DEFAULT_INVALIDATION_DEBOUNCE_MS
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY_INVALIDATION_DEBOUNCE_MS)
    if (raw == null || typeof raw !== 'string' || raw.length === 0 || raw.length > 20) {
      return DEFAULT_INVALIDATION_DEBOUNCE_MS
    }
    const n = Number(raw)
    if (!Number.isFinite(n)) return DEFAULT_INVALIDATION_DEBOUNCE_MS
    const clamped = Math.round(n)
    return Math.min(MAX_DEBOUNCE_MS, Math.max(MIN_DEBOUNCE_MS, clamped))
  } catch {
    return DEFAULT_INVALIDATION_DEBOUNCE_MS
  }
}

/**
 * Base URL for the realtime SSE worker.
 * Priority: localStorage (runtime override) → DefinePlugin (build-time) → same-origin ('').
 * - localStorage key: realtime_sse_base_url — set in console for review env debugging.
 * - DefinePlugin: window.REALTIME_SSE_BASE_URL (omit or '' for same-origin).
 */
export function getRealtimeSSEBaseUrl(): string {
  const fromStorage = readBaseUrlFromStorage()
  if (fromStorage) return fromStorage
  const fromWindow = typeof window !== 'undefined' && window.REALTIME_SSE_BASE_URL
  return (fromWindow && String(fromWindow)) || ''
}

/**
 * Path for the subscribe endpoint.
 * Priority: localStorage (runtime override) → DefinePlugin (build-time) → debug default.
 * - localStorage key: realtime_sse_subscribe_path — set in console for review env debugging.
 */
export function getRealtimeSSESubscribePath(): string {
  const fromStorage = readSubscribePathFromStorage()
  if (fromStorage) return fromStorage
  const path = typeof window !== 'undefined' && window.REALTIME_SSE_SUBSCRIBE_PATH
  return (path && String(path)) || '/api/realtime/subscribe'
}

/**
 * Set base URL override in localStorage and dispatch config change event.
 * Use in console for review env: setRealtimeSSEBaseUrl('http://localhost:8787')
 * Clears override when passed empty string or undefined.
 */
export function setRealtimeSSEBaseUrl(url: string | undefined): void {
  if (typeof window === 'undefined') return
  try {
    if (url && url.length > 0) {
      window.localStorage?.setItem(STORAGE_KEY_BASE_URL, url)
    } else {
      window.localStorage?.removeItem(STORAGE_KEY_BASE_URL)
    }
    window.dispatchEvent(new CustomEvent(CONFIG_CHANGE_EVENT))
  } catch {
    // ignore
  }
}

/**
 * Set subscribe path override in localStorage and dispatch config change event.
 * Use in console for review env: setRealtimeSSESubscribePath('/subscribe')
 * Clears override when passed empty string or undefined.
 */
export function setRealtimeSSESubscribePath(path: string | undefined): void {
  if (typeof window === 'undefined') return
  try {
    if (path && path.length > 0) {
      window.localStorage?.setItem(STORAGE_KEY_SUBSCRIBE_PATH, path)
    } else {
      window.localStorage?.removeItem(STORAGE_KEY_SUBSCRIBE_PATH)
    }
    window.dispatchEvent(new CustomEvent(CONFIG_CHANGE_EVENT))
  } catch {
    // ignore
  }
}

/**
 * Invalidation debounce (ms) used after an SSE event before refetch. Clamped to 0–30s.
 * Read from localStorage key realtime_sse_invalidation_debounce_ms; invalid values fall back to default (5000).
 */
export function getRealtimeSSEInvalidationDebounceMs(): number {
  return readInvalidationDebounceMsFromStorage()
}

/**
 * Set invalidation debounce override in localStorage and dispatch config change event.
 * Value is clamped to 0–30000 ms (0–30s). Pass undefined to clear override and use default.
 */
export function setRealtimeSSEInvalidationDebounceMs(ms: number | undefined): void {
  if (typeof window === 'undefined') return
  try {
    if (ms !== undefined) {
      const clamped = Math.min(MAX_DEBOUNCE_MS, Math.max(MIN_DEBOUNCE_MS, Math.round(ms)))
      window.localStorage?.setItem(STORAGE_KEY_INVALIDATION_DEBOUNCE_MS, String(clamped))
    } else {
      window.localStorage?.removeItem(STORAGE_KEY_INVALIDATION_DEBOUNCE_MS)
    }
    window.dispatchEvent(new CustomEvent(CONFIG_CHANGE_EVENT))
  } catch {
    // ignore
  }
}

/**
 * Hook that connects to the realtime SSE worker and invalidates RTK Query
 * cache when meeting/event invalidation messages are received.
 * Use on the meetings realtime page or the legacy meetings page (when realtime features enabled).
 *
 * When enabled, also writes connection state to sse_polling_state via setSSEConnectedForPolling
 * so consumers (e.g. right rail polling on the legacy page) can use relaxed polling when connected.
 *
 * For review env debugging: set localStorage realtime_sse_base_url, realtime_sse_subscribe_path,
 * realtime_sse_invalidation_debounce_ms (0–30000, default 5000); or use the setter helpers
 * for immediate reconnect / debounce update without refresh.
 */
export function useRealtimeSSEIntegration(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options
  const dispatch = useDispatch()

  // Track config in state so localStorage changes trigger reconnect / debounce update (no refresh needed).
  const [baseUrl, setBaseUrl] = useState(getRealtimeSSEBaseUrl)
  const [subscribePath, setSubscribePath] = useState(getRealtimeSSESubscribePath)
  const [invalidationDebounceMs, setInvalidationDebounceMs] = useState(getRealtimeSSEInvalidationDebounceMs)
  useEffect(() => {
    const onConfigChange = () => {
      setBaseUrl(getRealtimeSSEBaseUrl())
      setSubscribePath(getRealtimeSSESubscribePath())
      setInvalidationDebounceMs(getRealtimeSSEInvalidationDebounceMs())
    }
    window.addEventListener(CONFIG_CHANGE_EVENT, onConfigChange)
    window.addEventListener('storage', onConfigChange)
    return () => {
      window.removeEventListener(CONFIG_CHANGE_EVENT, onConfigChange)
      window.removeEventListener('storage', onConfigChange)
    }
  }, [])

  // Stable handler reference so useRealtimeSSE's effect doesn't reconnect every render.
  // Debounce invalidation per resource so the API has time to update before we refetch (SSE can
  // arrive first). Each resource has its own timer (configurable via localStorage, 0–30s).
  const onResourceInvalidated = useMemo(() => {
    const debounceMs = invalidationDebounceMs
    const invalidationHandler = createRTKQueryInvalidationHandler(
      meetingsGraphqlApi as any,
      dispatch as (action: unknown) => void,
      (resourceType: string, resourceId: string) => (
        (resourceType?.toLowerCase() === 'meeting' || resourceType?.toLowerCase() === 'event')
          ? [buildMeetingTag(resourceId), buildMeetingListTag()]
          : []
      ),
    )
    const pending = new Map<string, ResourceInvalidation>()
    const timeouts = new Map<string, ReturnType<typeof setTimeout>>()

    const flushOne = (key: string) => {
      const invalidation = pending.get(key)
      timeouts.delete(key)
      pending.delete(key)
      if (invalidation) invalidationHandler(invalidation)
    }

    const schedule = (invalidation: ResourceInvalidation) => {
      const key = `${invalidation.resourceType}:${invalidation.resourceId}`
      pending.set(key, invalidation)
      const existing = timeouts.get(key)
      if (existing != null) clearTimeout(existing)
      timeouts.set(
        key,
        setTimeout(() => flushOne(key), debounceMs),
      )
    }

    schedule.cancel = () => {
      for (const id of timeouts.values()) clearTimeout(id)
      timeouts.clear()
      pending.clear()
    }

    return schedule
  }, [dispatch, invalidationDebounceMs])

  useEffect(() => () => onResourceInvalidated.cancel(), [onResourceInvalidated])

  const result = useRealtimeSSE({
    baseUrl,
    subscribePath,
    onResourceInvalidated,
    enabled,
  })

  // Sync connection state to shared polling state so right rail and list can use relaxed cadence.
  // Only write when we have authoritative state for this tab. Do not clear on unmount: localStorage
  // is shared across tabs; clearing here would make other tabs with active SSE switch to aggressive polling.
  useEffect(() => {
    if (enabled) {
      const connected = result.connectionState === 'connected'
      setSSEConnectedForPolling(connected ? true : undefined)
    } else {
      setSSEConnectedForPolling(false)
    }
  }, [enabled, result.connectionState])

  return result
}
